<template>
  <div class="controls">
    <div class="controls__start">
      <span class="controls__count">
        {{ instanceCount && selectedInstances && selectedInstances.length > 0 ? `Instances (${selectedInstances.length} / ${instanceCount})` :`Instances (${instanceCount})` }}
      </span>
      <HelpTip>
        Cloud Instance List displays all ongoing operations that require cloud instances.
        Operations will automatically terminate upon completion. However, you can manually terminate any operation from this screen.
      </HelpTip>
    </div>
    <div class="controls__end">
      <div class="controls__actions">
        <button class="button" @click="handleRefreshClicked">
          <SVGIcon
            :iconName="'refresh'"
            :width="'18px'"
            :height="'18px'"
          />Refresh
        </button>
        <button class="button delete" :disabled="selectedInstances && selectedInstances.length === 0" @click="handleStopClicked">
          <SVGIcon
            :iconName="'stop'"
            :width="'18px'"
            :height="'18px'"
          />
          Stop
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { toRefs, watch } from 'vue';
import SVGIcon from '@/components/SVGIcon.vue';
import HelpTip from '../HelpTip.vue';

const props = defineProps({
  name: { type: String, default: 'null' },
  state: { type: String, default: 'null' },
  status: { type: String, default: 'null' },
  ip: { type: String, default: 'null' },
  type: { type: String, default: 'null' },
  date: { type: String, default: 'null' },
  instanceCount: { type: Number, default: 0 },
  selectedInstances: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'stop-clicked',
  'refresh-clicked',
  'start-clicked',
]);

const {
  name, state, status, ip, type,
  date, instanceCount, selectedInstances,
} = toRefs(props);

function handleStopClicked() {
  emit('stop-clicked');
}

function handleRefreshClicked() {
  emit('refresh-clicked');
}

function handleStartClicked() {
  emit('start-clicked');
}

</script>

<style lang="scss" scoped>
.controls {
  display: flex;
  margin: 20px 20px 0 20px;
  align-items: center;
  gap: 10px;

  &__count {
    font-weight: bold;
    font-size: 1rem;
  }

  &__start {
    display: flex;
    justify-content: flex-start;
    width: fit-content;
    gap: 10px;
  }

  &__end {
    display: flex;
    justify-content: flex-end;
    flex: 1;
  }

  &__actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    height: 100%;
  }
}

@media (max-width: $breakpoint-sm) {
  .controls {
    flex-direction: column;
    &__start{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__middle{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__end{
      display: flex;
      align-items: flex-end;
      flex-direction: column;
      width: 100%;
    }
  }
}

.button {
    background: white;
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 26px;
    padding: 0;
    box-shadow: 1px 2px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 1);
    padding: 4px 8px;
    gap: 2px;
    width: fit-content;

    @include themify() {
      color: themed('body-text-color')
    }

    &.highlight {
      background: var(--button-color);
      color: var(--button-text-color);

      svg {
        margin-right: 5px;
      }
    }

    &:hover {
      background: rgba(var(--color-primary-100-rgb), 0.5);
    }

    &.delete:not([disabled]) {
      @include themify() {
        background: themed('button-delete');
        color: themed('button-delete-text');
      }
    }

    &.delete[disabled] {
      border: none;
    }

    &.delete:hover:not([disabled]) {
      @include themify() {
        background: $color-red-300;
        color: themed('button-delete-text');
      }
    }
  }
</style>
